import { Fragment, ReactNode } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils';

export type PageHeaderProps = {
    title: string | ReactNode;
    className?: string | object;
    breadcrumbs?: ReactNode | null;
    subtitle?: string | string[] | null;
    subtitleSeparator?: boolean;
    actions?: ReactNode | null;
    backButton?: HeaderBackButton;
};

type HeaderBackButton = null | false | true | Omit<BackButtonProps, 'label'>;

export default function PageHeader({
    breadcrumbs = null,
    title,
    subtitle = null,
    actions = null,
    className,
    subtitleSeparator = true,
    backButton = false,
    ...rest
}: PageHeaderProps) {
    const backButtonConfig: BackButtonProps | null =
        backButton === true
            ? { label: title }
            : !backButton
              ? null
              : { ...backButton, label: title };
    return (
        <div
            {...rest}
            className={cn(
                'alli-flex alli-w-full alli-items-center alli-justify-between',
                className,
            )}>
            {backButtonConfig ? (
                <BackButton {...backButtonConfig} />
            ) : (
                <div className="alli-min-w-0 alli-flex-1">
                    {breadcrumbs}
                    <Title title={title} />
                    {subtitle && (
                        <Subtitle
                            items={
                                typeof subtitle === 'string'
                                    ? [subtitle]
                                    : subtitle
                            }
                            separator={subtitleSeparator}
                        />
                    )}
                </div>
            )}

            {actions && (
                <div className="alli-mt-0 alli-flex alli-space-x-3 lg:alli-ml-4">
                    {Array.isArray(actions)
                        ? actions.map((action, index) => (
                              <Fragment key={index}>{action}</Fragment>
                          ))
                        : actions}
                </div>
            )}
        </div>
    );
}

type SubtitleProp = {
    items?: string[] | null;
    separator: boolean;
};

function Subtitle({ items, separator = true }: SubtitleProp) {
    return (
        <div className="alli-mt-1 alli-flex alli-space-x-1.5 alli-text-sm alli-font-medium alli-leading-5 alli-text-gray-700 sm:alli-flex-row">
            {items &&
                items?.map((item, index) => {
                    return (
                        <Fragment key={`subtitle-${index}`}>
                            {index > 0 && separator && <Hyphen />}
                            <div className="alli-mt-2">{item}</div>
                        </Fragment>
                    );
                })}
        </div>
    );
}

function Hyphen() {
    return <span className="alli-mt-2">&#8209;</span>;
}

function Title({ title }: { title: string | ReactNode }) {
    if (typeof title === 'string') {
        return (
            <h2 className="alli-mt-1 alli-text-2xl alli-font-medium alli-leading-7 alli-text-gray-900 sm:alli-truncate sm:alli-tracking-tight">
                {title}
            </h2>
        );
    }
    return <>{title}</>;
}

type BackButtonProps = {
    onClick?: () => void;
    label: string | ReactNode;
};

export function BackButton({
    onClick,
    label,
}: BackButtonProps): JSX.Element | null {
    const handleClick = () => {
        if (onClick) {
            onClick();
        } else {
            window.history.back();
        }
    };

    const backButton = (
        <button
            data-testid="back-button"
            type="button"
            onClick={handleClick}
            className="alli-mt-1 alli-flex alli-h-5 alli-w-5 alli-shrink-0 alli-items-center alli-justify-center"
            aria-label="Back">
            <ArrowLeftIcon className="alli-h-5 alli-w-5" />
        </button>
    );

    return (
        <div
            className="alli-flex alli-items-center alli-space-x-2"
            data-testid="back-button-container">
            {backButton}
            <Title title={label} />
        </div>
    );
}
